import React, { useCallback, useEffect, useState } from 'react';
import ComingSoonSplash from './ComingSoonSplash';

const API_BASE = process.env.REACT_APP_API_BASE || '';
const CACHE_KEY = 'launchGate';

function readCache() {
  try {
    const raw = localStorage.getItem(CACHE_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw);
    if (parsed && typeof parsed === 'object') return parsed;
    return null;
  } catch {
    return null;
  }
}

function writeCache(value) {
  try {
    localStorage.setItem(CACHE_KEY, JSON.stringify(value));
  } catch {
    // localStorage unavailable; ignore
  }
}

function LaunchGate({ user, children }) {
  // 'unknown' = first render before we've decided.
  // 'unlocked' = render children.
  // { launchAt, skewMs } = render splash.
  const [state, setState] = useState(() => {
    if (user) return 'unlocked';
    const cache = readCache();
    if (!cache) return 'unknown';
    if (cache.unlocked === true) return 'unlocked';
    const cachedLaunchMs = cache.launch_at ? Date.parse(cache.launch_at) : NaN;
    if (Number.isNaN(cachedLaunchMs)) {
      return 'unknown';
    }
    if (Date.now() + (cache.skew_ms || 0) >= cachedLaunchMs) {
      return 'unlocked';
    }
    return { launchAt: cache.launch_at, skewMs: cache.skew_ms || 0 };
  });

  const fetchStatus = useCallback(async () => {
    try {
      const resp = await fetch(`${API_BASE}/api/public-status`);
      if (!resp.ok) throw new Error(`status ${resp.status}`);
      const data = await resp.json();
      if (!data.launch_at) {
        writeCache({ unlocked: true });
        setState('unlocked');
        return;
      }
      const serverMs = Date.parse(data.server_now);
      const launchMs = Date.parse(data.launch_at);
      if (Number.isNaN(launchMs) || Number.isNaN(serverMs)) {
        writeCache({ unlocked: true });
        setState('unlocked');
        return;
      }
      if (serverMs >= launchMs) {
        writeCache({ unlocked: true });
        setState('unlocked');
        return;
      }
      const skewMs = serverMs - Date.now();
      writeCache({ unlocked: false, launch_at: data.launch_at, skew_ms: skewMs });
      setState({ launchAt: data.launch_at, skewMs });
    } catch {
      // Fail open — never let a transient API blip take down a launched site.
      setState('unlocked');
    }
  }, []);

  useEffect(() => {
    if (state === 'unknown' && !user) {
      fetchStatus();
    } else if (state === 'unlocked' && !user) {
      const cache = readCache();
      if (!cache || cache.unlocked !== true) {
        writeCache({ unlocked: true });
      }
    }
  }, [state, user, fetchStatus]);

  const handleLaunched = useCallback(() => {
    fetchStatus();
  }, [fetchStatus]);

  if (user) return children;
  if (state === 'unlocked') return children;
  if (state === 'unknown') return null;
  return (
    <ComingSoonSplash
      launchAt={state.launchAt}
      skewMs={state.skewMs}
      onLaunched={handleLaunched}
    />
  );
}

export default LaunchGate;
